import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { usuarios, type Perfil, type UsuarioNaLista } from '../api/acesso'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { formatar } from '../i18n'
import './usuarios.css'

const PERFIS: Perfil[] = ['Leitura', 'Operacao', 'Administrador']

type Edicao =
  | { modo: 'fechado' }
  | { modo: 'novo' }
  | { modo: 'editando'; usuario: UsuarioNaLista }

export function Usuarios() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const [lista, setLista] = useState<UsuarioNaLista[] | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [edicao, setEdicao] = useState<Edicao>({ modo: 'fechado' })
  const [recado, setRecado] = useState<string | null>(null)

  const gerencia = pode('UsuarioGerenciar')

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setLista(await usuarios.listar())
    } catch {
      setFalhou(true)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function revogar(usuario: UsuarioNaLista) {
    const { encerradas } = await usuarios.revogarSessoes(usuario.id)
    setRecado(t('usuarios.sessoesRevogadas', { total: encerradas }))
    await carregar()
  }

  async function reenviar(usuario: UsuarioNaLista) {
    try {
      await usuarios.reenviarConvite(usuario.id)
      setRecado(t('usuarios.conviteReenviado'))
    } catch {
      setRecado(t('usuarios.conviteFalhou'))
    }
  }

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!lista) return <Estado tipo="carregando" />

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('usuarios.titulo')}</h1>
          <p className="usuarios__descricao">{t('usuarios.descricao')}</p>
        </div>

        {/* Esconder o botão é conveniência; quem recusa a criação é o
            servidor, que responde 403 mesmo se alguém chamar direto. */}
        {gerencia && (
          <Botao variante="primario" onClick={() => setEdicao({ modo: 'novo' })}>
            {t('usuarios.novo')}
          </Botao>
        )}
      </header>

      {recado && <Aviso tom="info">{recado}</Aviso>}

      {lista.length === 0 ? (
        <Estado tipo="vazio" />
      ) : (
        <div
            className="usuarios__tabela-rolagem"
            // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
            // sem isto, a parte escondida da tabela não existe para quem não usa mouse.
            tabIndex={0}
            role="group"
            aria-label={t('usuarios.legenda')}
          >
            <table className="usuarios__tabela">
            <caption className="usuarios__legenda">{t('usuarios.legenda')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('usuarios.nome')}</th>
                <th scope="col">{t('acesso.email')}</th>
                <th scope="col">{t('usuarios.perfil')}</th>
                <th scope="col">{t('usuarios.situacao')}</th>
                <th scope="col">{t('usuarios.ultimoAcesso')}</th>
                <th scope="col">{t('usuarios.acoes')}</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((u) => (
                <tr key={u.id}>
                  <td>{u.nome}</td>
                  <td>{u.email}</td>
                  <td>{t(`perfil.${u.perfil}`)}</td>
                  <td>
                    {/* Texto, não só cor (UI-09). */}
                    <span className={`etiqueta etiqueta--${u.ativo ? 'ativo' : 'inativo'}`}>
                      {u.ativo ? t('usuarios.ativo') : t('usuarios.inativo')}
                    </span>
                    {!u.senhaDefinida && (
                      <span className="etiqueta etiqueta--pendente">
                        {t('usuarios.convitePendente')}
                      </span>
                    )}
                  </td>
                  <td>{u.ultimoAcessoEm ? formatar.dataHora(u.ultimoAcessoEm) : '—'}</td>
                  <td className="usuarios__acoes-linha">
                    {gerencia && (
                      <Botao
                        variante="discreto"
                        onClick={() => setEdicao({ modo: 'editando', usuario: u })}
                      >
                        {t('usuarios.editar')}
                      </Botao>
                    )}
                    {gerencia && !u.senhaDefinida && (
                      <Botao variante="discreto" onClick={() => reenviar(u)}>
                        {t('usuarios.reenviarConvite')}
                      </Botao>
                    )}
                    {pode('SessaoGerenciar') && u.sessoesAbertas > 0 && (
                      <Botao variante="discreto" onClick={() => revogar(u)}>
                        {t('usuarios.revogar', { total: u.sessoesAbertas })}
                      </Botao>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edicao.modo !== 'fechado' && (
        <Formulario
          usuario={edicao.modo === 'editando' ? edicao.usuario : null}
          aoFechar={() => setEdicao({ modo: 'fechado' })}
          aoSalvar={async (aviso) => {
            setEdicao({ modo: 'fechado' })
            setRecado(aviso)
            await carregar()
          }}
        />
      )}
    </main>
  )
}

function Formulario({
  usuario,
  aoFechar,
  aoSalvar,
}: {
  usuario: UsuarioNaLista | null
  aoFechar: () => void
  aoSalvar: (aviso: string) => Promise<void>
}) {
  const { t } = useTranslation()

  const [nome, setNome] = useState(usuario?.nome ?? '')
  const [email, setEmail] = useState(usuario?.email ?? '')
  const [perfil, setPerfil] = useState<Perfil>(usuario?.perfil ?? 'Leitura')
  const [segundoFator, setSegundoFator] = useState(usuario?.segundoFatorAtivo ?? false)
  const [ativo, setAtivo] = useState(usuario?.ativo ?? true)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // Administrador sempre usa segundo fator; o servidor impõe isso de todo jeito.
  // Mostrar a caixa habilitada sugeriria uma escolha que não existe.
  const forcado = perfil === 'Administrador'

  async function salvar(evento: FormEvent) {
    evento.preventDefault()
    setErro(null)
    setEnviando(true)

    try {
      if (usuario) {
        await usuarios.alterar(usuario.id, {
          nome,
          perfil,
          segundoFatorAtivo: forcado || segundoFator,
          ativo,
        })
        await aoSalvar(t('usuarios.salvo'))
      } else {
        const { convite } = await usuarios.criar({
          email,
          nome,
          perfil,
          segundoFatorAtivo: forcado || segundoFator,
        })

        await aoSalvar(
          convite === 'enviado' ? t('usuarios.criadoComConvite') : t('usuarios.criadoSemConvite'),
        )
      }
    } catch (e) {
      const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
      setErro(corpo?.detail ?? t('comum.erroDescricao'))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Cartao titulo={usuario ? t('usuarios.editarTitulo') : t('usuarios.novoTitulo')}>
      <form onSubmit={salvar} noValidate>
        {erro && <Aviso tom="perigo">{erro}</Aviso>}

        <Campo
          rotulo={t('usuarios.nome')}
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          obrigatorio
          autoFocus
        />

        <Campo
          rotulo={t('acesso.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          // O e-mail é a identidade do login. Trocá-lo em cadastro existente
          // seria trocar de pessoa mantendo o histórico de auditoria — então
          // aqui ele é imutável.
          disabled={!!usuario}
          ajuda={usuario ? t('usuarios.emailImutavel') : t('usuarios.emailAjuda')}
          obrigatorio
        />

        <fieldset className="usuarios__grupo">
          <legend>{t('usuarios.perfil')}</legend>
          {PERFIS.map((p) => (
            <label key={p} className="usuarios__opcao">
              <input
                type="radio"
                name="perfil"
                value={p}
                checked={perfil === p}
                onChange={() => setPerfil(p)}
              />
              <span>
                <strong>{t(`perfil.${p}`)}</strong>
                <small>{t(`perfil.${p}Descricao`)}</small>
              </span>
            </label>
          ))}
        </fieldset>

        <label className="usuarios__opcao">
          <input
            type="checkbox"
            checked={forcado || segundoFator}
            disabled={forcado}
            onChange={(e) => setSegundoFator(e.target.checked)}
          />
          <span>
            <strong>{t('usuarios.segundoFator')}</strong>
            <small>{forcado ? t('usuarios.segundoFatorForcado') : t('usuarios.segundoFatorAjuda')}</small>
          </span>
        </label>

        {usuario && (
          <label className="usuarios__opcao">
            <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
            <span>
              <strong>{t('usuarios.ativoRotulo')}</strong>
              <small>{t('usuarios.ativoAjuda')}</small>
            </span>
          </label>
        )}

        <div className="usuarios__acoes-formulario">
          <Botao type="submit" variante="primario" carregando={enviando}>
            {t('comum.salvar')}
          </Botao>
          <Botao variante="discreto" onClick={aoFechar}>
            {t('comum.cancelar')}
          </Botao>
        </div>
      </form>
    </Cartao>
  )
}
